import { FormKit } from "@formkit/vue"
import { vReqT, assertIs, assertNonNull, exhaustiveCaseGuard, UiOption, UiOptions, noAvailableOptions, forceCheckedIndexedAccess, Reflike, unreachable, SetEx } from "src/helpers/utils"
import { defineComponent, ref } from "vue"
import { ClientArgValue, fkbs_defensiveCopy, MungedArgDefOptions_List, MungedArgDefOptions_Tree, MungedArgDefOptions_TreeNode } from "./ClientQueryData"
import { FormkitSpreadBugUnnecessaryGroup, defaultQueryFormNameAndValidation } from "./ReportBuilder.02"
import { ArgDefType } from "./ReportBuilder.io"

export const ArgFormElement = defineComponent({
  props: {
    type: vReqT<ArgDefType>(),
    options_calleeDefensivelyCopies: vReqT<MungedArgDefOptions_List | MungedArgDefOptions_Tree | null>(),
    label: vReqT<string | undefined>(),
    value: vReqT<ClientArgValue>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    /**
     * values are {value: null | V} for some `V`, but this component should NOT be given values that are null,
     * because that means "is not accepting input for this form elem".
     * So this asserts that the value for some obj is not null
     */
    const checkedHasNonNullValue = <V, T extends {value: V}>(obj: T) : T & {value: V & {}} => {
      assertNonNull(obj.value)
      return obj as any
    }

    const getSingleArgOrFail = () => {
      assertIs(props.value.type, "single")
      return checkedHasNonNullValue(props.value)
    }

    const getMultiArgOrFail = () => {
      assertIs(props.value.type, "multi")
      return checkedHasNonNullValue(props.value)
    }

    const getTreeArgOrFail = () => {
      assertIs(props.value.type, "tree")
      return checkedHasNonNullValue(props.value)
    }

    const getOptionsListOrFail = () => {
      assertNonNull(props.options_calleeDefensivelyCopies)
      assertIs(props.options_calleeDefensivelyCopies.type, "list")
      return props.options_calleeDefensivelyCopies
    }

    const getOptionsTreeOrFail = () => {
      assertNonNull(props.options_calleeDefensivelyCopies)
      assertIs(props.options_calleeDefensivelyCopies.type, "tree")
      return props.options_calleeDefensivelyCopies
    }

    const didUpdate = () : void => {
      ctx.emit("didUpdate");
    }

    return () => {
      switch (props.type) {
        case "text": {
          const value = getSingleArgOrFail()
          return <Arg_Text
            value={value}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        case "date": {
          const value = getSingleArgOrFail()
          return <Arg_Date
            value={value}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        case "number": {
          const value = getSingleArgOrFail()
          return <Arg_Number
            value={value}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        case "radio": {
          const value = getSingleArgOrFail()
          const options = getOptionsListOrFail()
          return <Arg_Radio
            value={value}
            options={fkbs_defensiveCopy(options.options)}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        case "checkbox": {
          const value = getMultiArgOrFail()
          const options = getOptionsListOrFail()
          return <Arg_Checkbox
            value={value}
            options={fkbs_defensiveCopy(options.options)}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        case "select": {
          assertNonNull(props.options_calleeDefensivelyCopies)
          assertIs(props.options_calleeDefensivelyCopies.type, "list")
          const value = getSingleArgOrFail()
          const options = getOptionsListOrFail()
          return <Arg_SelectListOne
            value={value}
            options={fkbs_defensiveCopy(options.options)}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        case "menu-tree": {
          assertNonNull(props.options_calleeDefensivelyCopies)
          assertIs(props.options_calleeDefensivelyCopies.type, "tree")

          const value = getTreeArgOrFail()
          const options = getOptionsTreeOrFail()

          // n.b. no defensive copy of options here because the implementation does some processing that
          // is expected to perform the copy.
          return <Arg_SelectTreeOne
            value={value.value}
            levels={options.levels}
            rootLevel={options.children}
            onDidUpdate={didUpdate}
          />
        }
        case "select-many": {
          const value = getMultiArgOrFail()
          return <Arg_SelectListMany
            value={value}
            options={fkbs_defensiveCopy(getOptionsListOrFail().options)}
            label={props.label || ""}
            onDidUpdate={didUpdate}
          />
        }
        default: exhaustiveCaseGuard(props.type)
      }
    }
  }
})

const handleStringUpdate = (old: string, fresh: unknown, onDidUpdate: (positivelyChangedValue: string) => void) : void => {
  if (typeof fresh !== "string" && typeof fresh !== "number") {
    unreachable()
  }

  if (old === fresh) {
    return
  }

  onDidUpdate(fresh.toString())
}

const handleStringArrayUpdate = (old: string[], fresh: unknown, onDidUpdate: (positivelyChangedValue: string[]) => void) : void => {
  if (!Array.isArray(fresh)) {
    unreachable()
  }

  const freshStrings = fresh.map(v => v.toString())

  if (new SetEx(old).isSame(freshStrings)) {
    return
  }

  onDidUpdate(freshStrings)
}

const Arg_Text = defineComponent({
  props: {
    value: vReqT<Reflike<string>>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="text"
        v-model={props.value.value}
        {...defaultQueryFormNameAndValidation}
        autocomplete="off"
        label={props.label}
        onInput={(v: unknown) => handleStringUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_Date = defineComponent({
  props: {
    value: vReqT<Reflike<string>>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="date"
        v-model={props.value.value}
        {...defaultQueryFormNameAndValidation}
        label={props.label}
        onInput={(v: unknown) => handleStringUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_Number = defineComponent({
  props: {
    value: vReqT<Reflike<string>>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="number"
        v-model={props.value.value}
        {...defaultQueryFormNameAndValidation}
        label={props.label}
        onInput={(v: unknown) => handleStringUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_Radio = defineComponent({
  props: {
    value: vReqT<Reflike<string>>(),
    options: vReqT<UiOption[]>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="radio"
        v-model={props.value.value}
        options={props.options}
        {...defaultQueryFormNameAndValidation}
        label={props.label}
        onInput={(v: unknown) => handleStringUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_Checkbox = defineComponent({
  props: {
    value: vReqT<Reflike<string[]>>(),
    options: vReqT<UiOption[]>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="checkbox"
        v-model={props.value.value}
        options={props.options}
        {...defaultQueryFormNameAndValidation}
        label={props.label}
        onInput={(v: unknown) => handleStringArrayUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_SelectListOne = defineComponent({
  props: {
    value: vReqT<Reflike<string>>(),
    options: vReqT<UiOption[]>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="select"
        v-model={props.value.value}
        options={props.options}
        {...defaultQueryFormNameAndValidation}
        label={props.label}
        placeholder="Select an Option"
        onInput={(v: unknown) => handleStringUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_SelectListMany = defineComponent({
  props: {
    value: vReqT<Reflike<string[]>>(),
    options: vReqT<UiOption[]>(),
    label: vReqT<string>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    return () => <FormkitSpreadBugUnnecessaryGroup>
      <FormKit
        type="select"
        multiple={true}
        size="4"
        v-model={props.value.value}
        options={props.options}
        {...defaultQueryFormNameAndValidation}
        label={props.label}
        onInput={(v: unknown) => handleStringArrayUpdate(
          props.value.value,
          v,
          (fresh) => {
            props.value.value = fresh
            ctx.emit("didUpdate")
          }
        )}
      />
    </FormkitSpreadBugUnnecessaryGroup>
  }
})

const Arg_SelectTreeOne = defineComponent({
  props: {
    value: vReqT<{[name: string]: string}>(),
    levels: vReqT<{label: string, name: string}[]>(),
    rootLevel: vReqT<MungedArgDefOptions_TreeNode[]>(),
  },
  emits: {
    didUpdate: () => true,
  },
  setup(props, ctx) {
    const computeOptions = () => {
      let workingNodeList : MungedArgDefOptions_TreeNode[] | null = props.rootLevel

      const result : {level: {label: string, name: string}, options: UiOptions}[] = []

      for (let i = 0; i < props.levels.length; i++) {
        if (workingNodeList) {
          result.push({
            level: props.levels[i],
            options: workingNodeList.length === 0
              ? noAvailableOptions()
              : {
                disabled: false,
                options: fkbs_defensiveCopy(workingNodeList.map(v => v.data))
              }
          })
        }
        else {
          result.push({
            level: props.levels[i],
            options: i === 0
              ? noAvailableOptions()
              : noAvailableOptions(`Select an option for ${props.levels[i-1].label}`)
          })
        }

        const name = props.levels[i].name
        const userValue = props.value[name]
        workingNodeList = workingNodeList?.find(v => v.data.value /*weakEq*/ == userValue)?.children ?? null
      }

      return result;
    }

    const recomputeSelections = () : void => {
      treeOptions.value = computeOptions()

      let workingNodeList : MungedArgDefOptions_TreeNode[] | undefined = props.rootLevel

      for (let i = 0; i < props.levels.length; i++) {
        const name = props.levels[i].name
        const userValue = props.value[name]

        const nextNodeList : MungedArgDefOptions_TreeNode | undefined = workingNodeList?.find(v => v.data.value /*weakEq*/ == userValue)

        if (nextNodeList) {
          workingNodeList = nextNodeList?.children
        }
        else {
          props.value[name] = forceCheckedIndexedAccess(workingNodeList ?? [], 0)?.data.value || ""
          workingNodeList = undefined

          // recompute "next options" based on revised selection, and then keep going
          treeOptions.value = computeOptions()
        }
      }
    }

    const treeOptions = ref(computeOptions())

    return () => {
      return <div>
        <div
          // a tree with 1 level should look like a typical plain-ol' select list
          // but with more than 1 level we try group them together visually
          class={`inline-block ${props.levels.length > 1 ? "border p-1 rounded-md" : ""}`}
          style="min-width: var(--fk-max-width-input)"
        >
          {treeOptions.value.map((opt, i) => {
            const fkbs_sink = {
              get value() : string { return props.value[opt.level.name] },
              set value(fresh) {
                const old = props.value[opt.level.name]

                props.value[opt.level.name] = fresh
                recomputeSelections()

                handleStringUpdate(old, fresh, () => ctx.emit("didUpdate"))
              }
            }

            return <div class={i === 0 ? "" : "mt-2"}>
              <FormkitSpreadBugUnnecessaryGroup>
                <FormKit
                  type="select"
                  label={opt.level.label}
                  placeholder="Select an Option"
                  v-model={fkbs_sink.value}
                  disabled={opt.options.disabled}
                  options={opt.options.options}
                  {...defaultQueryFormNameAndValidation}
                />
              </FormkitSpreadBugUnnecessaryGroup>
            </div>
          })}
        </div>
      </div>
    }
  }
})
